import type { Experiment, Variant } from 'types';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { NavLink } from '@/components/NavLink';
import { ChevronDown, ArrowUpDown } from 'lucide-react';
import { isPreStart } from 'helpers/experiment-status';
import { RowActions } from './RowActions';
import { formatDate } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { FOCUS_RING, MICRO_LABEL_SHAPE } from '../ConsolePanel';
import { isAwaitingFirstVisitor } from './home-states';

/** Same tones as the desktop table, so one experiment reads the same on both. */
const STATUS_TONE: Record<string, string> = {
  Running: 'text-ok',
  Draft: 'text-dim',
  QA: 'text-sel',
  Deployed: 'text-accent',
};

type ExperimentsTableMobileProps = {
  experiments: Experiment[];
  view: string;
  sort: (key: keyof Experiment) => void;
};

export function ExperimentsOverviewTableMobile({ experiments, view, sort }: ExperimentsTableMobileProps) {
  const [expanded, setExpanded] = useState<number | null>(null);

  const toggleExpanded = (id: number) => () => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  if (experiments.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 p-3.5">
      <div className="flex items-center gap-4">
        <SortHead label="ID" onClick={() => sort('id')} />
        {view === 'All' && <SortHead label="Status" onClick={() => sort('status')} />}
        {view !== 'Drafts' && <SortHead label="Sessions" onClick={() => sort('sessions')} />}
      </div>

      {experiments.map((experiment) => (
        <div key={experiment.id} className="border-line bg-panel rounded-card border">
          <div className="flex items-center gap-2.5 px-3 py-2">
            <span className="num text-dim shrink-0">{experiment.id}</span>
            <NavLink
              to={`/experiments/${experiment.id}`}
              title={experiment.name}
              className="min-w-0 flex-1 truncate text-meta"
            >
              {experiment.name}
            </NavLink>
            <span className={cn(MICRO_LABEL_SHAPE, 'shrink-0', STATUS_TONE[experiment.status ?? ''] ?? 'text-dim')}>
              {experiment.status}
            </span>
            {/*
              The chevron is the toggle, not the card.
              The whole card used to carry the onClick as a bare `div`, which made
              expanding a row mouse-only — no tab stop, no Enter/Space — and put the
              row's own NavLink and "..." menu inside the toggle's hit area, so
              opening the menu also collapsed the card behind it.
            */}
            <button
              type="button"
              onClick={toggleExpanded(experiment.id)}
              aria-expanded={expanded === experiment.id}
              aria-label={`${expanded === experiment.id ? 'Hide' : 'Show'} details for ${experiment.name}`}
              className={cn(
                'text-dim hover:bg-hover hover:text-foreground flex size-7 shrink-0 items-center justify-center',
                'rounded-lg transition-colors duration-150',
                FOCUS_RING,
              )}
            >
              <ChevronDown
                className={cn('size-4 transition-transform duration-150', expanded === experiment.id && 'rotate-180')}
                strokeWidth={1.5}
              />
            </button>
            <RowActions experiment={experiment} />
          </div>

          {expanded === experiment.id && (
            <div className="border-line grid grid-cols-2 gap-x-3 gap-y-2 border-t px-3 py-2.5">
              {!isPreStart(experiment.status) && (
                <Field label="Sessions">{isAwaitingFirstVisitor(experiment) ? 'Waiting' : experiment.sessions}</Field>
              )}
              <Field label="Created At">{formatDate(experiment.createdAt ?? 0)}</Field>
              {!isPreStart(experiment.status) && (
                <Field label="Starts At">{formatDate(experiment.startsAt ?? 0)}</Field>
              )}
              {experiment.status === 'Ended' && <Field label="Ended At">{formatDate(experiment.endsAt ?? 0)}</Field>}
              {experiment.status === 'Deployed' && (
                <Field label="Deployed At">
                  {formatDate(experiment.variants?.find((v: Variant) => v.deployed)?.deployed ?? 0)}
                </Field>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

type SortHeadProps = { label: string; onClick: () => void };

const SortHead = ({ label, onClick }: SortHeadProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'micro-label hover:text-foreground flex min-h-7 items-center gap-1 rounded-sm transition-colors duration-150',
      FOCUS_RING,
    )}
  >
    {label}
    <ArrowUpDown className="size-3" strokeWidth={1.5} />
  </button>
);

type FieldProps = { label: string; children: ReactNode };

const Field = ({ label, children }: FieldProps) => (
  <div className="grid gap-1">
    <span className="micro-label">{label}</span>
    <span className="num">{children}</span>
  </div>
);
